import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription, hasTier } from '../context/SubscriptionContext';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import TierGate from '../components/TierGate';
import axios from 'axios';
import { formatINR, formatVolume } from '../utils/formatters';
import { Coins, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { externalApi } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNDERLYINGS = [
  { symbol: 'NIFTY', name: 'Nifty 50' },
  { symbol: 'BANKNIFTY', name: 'Bank Nifty' },
];

export default function OptionsChain() {
  const { user, loading } = useAuth();
  const { tier, loading: subLoading } = useSubscription();
  const [underlying, setUnderlying] = useState('NIFTY');
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const loadChain = useCallback(async () => {
    if (!hasTier(tier, 'elite')) return;
    try {
      const [chainRes, anaRes] = await Promise.all([
        axios.get(`${API}/options/${underlying}`, { withCredentials: true }),
        externalApi.options(underlying).catch(() => ({ data: null })),
      ]);
      setData(chainRes.data);
      setAnalytics(anaRes.data);
    } catch {
      setData(null);
    }
  }, [underlying, tier]);

  useEffect(() => {
    if (user) loadChain();
  }, [user, loadChain]);

  if (loading || subLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}><div className="text-white">Loading...</div></div>;
  if (!user) return <Navigate to="/login" />;

  if (!hasTier(tier, 'elite')) {
    return (
      <Layout>
        <TierGate requiredTier="elite" feature="Options Chain">
          <div />
        </TierGate>
      </Layout>
    );
  }

  const pcrColor = data && data.pcr > 1 ? '#00FF66' : data && data.pcr < 0.8 ? '#FF3366' : '#FBBF24';
  const pcrLabel = data && data.pcr > 1 ? 'Bullish' : data && data.pcr < 0.8 ? 'Bearish' : 'Neutral';

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-6" data-testid="options-chain-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Coins size={40} weight="duotone" className="text-[#2563EB]" />
            <div>
              <h1 className="text-4xl font-heading font-black tracking-tighter text-white">Options Chain</h1>
              <p className="text-sm text-[#94A3B8] mt-1">NSE F&O • Strike-wise OI, IV, Greeks</p>
            </div>
          </div>
          <div className="flex gap-1 border border-white/10 p-1" data-testid="underlying-selector">
            {UNDERLYINGS.map(u => (
              <button
                key={u.symbol}
                onClick={() => setUnderlying(u.symbol)}
                className={`px-4 py-1.5 text-sm font-mono transition-colors ${underlying === u.symbol ? 'bg-[#2563EB] text-white' : 'text-[#94A3B8] hover:text-white'}`}
                data-testid={`underlying-${u.symbol}`}
              >
                {u.name}
              </button>
            ))}
          </div>
        </div>

        {data && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="options-summary">
              <div className="bg-[#12141A] border border-white/10 p-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Spot</div>
                <div className="text-2xl font-mono font-semibold text-white">
                  {data.spot_price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">ATM Strike</div>
                <div className="text-2xl font-mono font-semibold text-white">{data.atm_strike.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">PCR</div>
                <div className="text-2xl font-mono font-semibold" style={{ color: pcrColor }}>{data.pcr}</div>
                <div className="text-xs font-mono uppercase mt-0.5" style={{ color: pcrColor }}>{pcrLabel}</div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Max Pain</div>
                <div className="text-2xl font-mono font-semibold text-[#FBBF24]">{data.max_pain.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-[#12141A] border border-white/10 p-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-1">Expiry</div>
                <div className="text-lg font-mono font-semibold text-white">{data.expiry}</div>
              </div>
            </div>

            {/* OI Supports & Resistances from NSE analytics */}
            {analytics && (analytics.supports?.length > 0 || analytics.resistances?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="oi-analytics-block">
                <div className="bg-[#12141A] border border-white/10 p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#00FF66] mb-3 flex items-center gap-1">
                    <ArrowDown size={12} weight="fill" /> Top Support Strikes (Max PE OI)
                  </p>
                  <div className="space-y-1">
                    {(analytics.supports || []).map((s, i) => (
                      <div key={i} className="flex justify-between text-xs" data-testid={`option-support-${i}`}>
                        <span className="font-mono text-white">₹{s.strike.toLocaleString('en-IN')}</span>
                        <span className="font-mono text-[#00FF66]">{(s.pe_oi || 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#12141A] border border-white/10 p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF3366] mb-3 flex items-center gap-1">
                    <ArrowUp size={12} weight="fill" /> Top Resistance Strikes (Max CE OI)
                  </p>
                  <div className="space-y-1">
                    {(analytics.resistances || []).map((r, i) => (
                      <div key={i} className="flex justify-between text-xs" data-testid={`option-resistance-${i}`}>
                        <span className="font-mono text-white">₹{r.strike.toLocaleString('en-IN')}</span>
                        <span className="font-mono text-[#FF3366]">{(r.ce_oi || 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {analytics.data_source === 'synthetic_fallback' && (
                  <div className="lg:col-span-2 text-[10px] text-[#F59E0B] font-mono">
                    ⚠ NSE India blocks datacenter IPs — supports/resistances shown from computed distribution.
                  </div>
                )}
              </div>
            )}

            {/* Options Chain Table */}
            <div className="bg-[#12141A] border border-white/10 overflow-hidden" data-testid="options-table">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-[#0A0B0E]/50">
                      <th colSpan="6" className="text-center p-3 font-mono uppercase tracking-[0.2em] text-[#00FF66] border-r border-white/10">CALL (CE)</th>
                      <th className="p-3 font-mono uppercase tracking-[0.2em] text-white bg-[#0A0B0E]">Strike</th>
                      <th colSpan="6" className="text-center p-3 font-mono uppercase tracking-[0.2em] text-[#FF3366]">PUT (PE)</th>
                    </tr>
                    <tr className="border-b border-white/10 bg-[#0A0B0E]/30">
                      <th className="p-2 text-right font-mono text-[#94A3B8]">Δ</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">IV</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">OI</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">Vol</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">Chg</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8] border-r border-white/10">LTP</th>
                      <th className="p-2 text-center font-mono text-white bg-[#0A0B0E]">—</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">LTP</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">Chg</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">Vol</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">OI</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">IV</th>
                      <th className="p-2 text-right font-mono text-[#94A3B8]">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.chain.map((row) => {
                      const isATM = row.strike === data.atm_strike;
                      const isITMCall = data.spot_price > row.strike;
                      const isITMPut = data.spot_price < row.strike;
                      return (
                        <tr
                          key={row.strike}
                          className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors ${isATM ? 'bg-[#2563EB]/10' : ''}`}
                          data-testid={`strike-${row.strike}`}
                        >
                          {/* CE */}
                          <td className={`p-2 text-right font-mono ${isITMCall ? 'bg-[#00FF66]/[0.05]' : ''}`}>{row.ce.delta}</td>
                          <td className={`p-2 text-right font-mono text-[#94A3B8] ${isITMCall ? 'bg-[#00FF66]/[0.05]' : ''}`}>{row.ce.iv}%</td>
                          <td className={`p-2 text-right font-mono text-white ${isITMCall ? 'bg-[#00FF66]/[0.05]' : ''}`}>{formatVolume(row.ce.oi)}</td>
                          <td className={`p-2 text-right font-mono text-[#94A3B8] ${isITMCall ? 'bg-[#00FF66]/[0.05]' : ''}`}>{formatVolume(row.ce.volume)}</td>
                          <td className={`p-2 text-right font-mono ${row.ce.change >= 0 ? 'text-[#00FF66]' : 'text-[#FF3366]'} ${isITMCall ? 'bg-[#00FF66]/[0.05]' : ''}`}>{row.ce.change >= 0 ? '+' : ''}{row.ce.change}</td>
                          <td className={`p-2 text-right font-mono font-semibold text-white border-r border-white/10 ${isITMCall ? 'bg-[#00FF66]/[0.05]' : ''}`}>{row.ce.price}</td>
                          {/* Strike */}
                          <td className={`p-2 text-center font-mono font-semibold ${isATM ? 'text-[#2563EB]' : 'text-white'} bg-[#0A0B0E]`}>{row.strike.toLocaleString('en-IN')}</td>
                          {/* PE */}
                          <td className={`p-2 text-right font-mono font-semibold text-white ${isITMPut ? 'bg-[#FF3366]/[0.05]' : ''}`}>{row.pe.price}</td>
                          <td className={`p-2 text-right font-mono ${row.pe.change >= 0 ? 'text-[#00FF66]' : 'text-[#FF3366]'} ${isITMPut ? 'bg-[#FF3366]/[0.05]' : ''}`}>{row.pe.change >= 0 ? '+' : ''}{row.pe.change}</td>
                          <td className={`p-2 text-right font-mono text-[#94A3B8] ${isITMPut ? 'bg-[#FF3366]/[0.05]' : ''}`}>{formatVolume(row.pe.volume)}</td>
                          <td className={`p-2 text-right font-mono text-white ${isITMPut ? 'bg-[#FF3366]/[0.05]' : ''}`}>{formatVolume(row.pe.oi)}</td>
                          <td className={`p-2 text-right font-mono text-[#94A3B8] ${isITMPut ? 'bg-[#FF3366]/[0.05]' : ''}`}>{row.pe.iv}%</td>
                          <td className={`p-2 text-right font-mono ${isITMPut ? 'bg-[#FF3366]/[0.05]' : ''}`}>{row.pe.delta}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-[#64748B] mt-3 font-mono">
              ITM (In-The-Money) options highlighted • ATM strike in blue • Δ=Delta, IV=Implied Volatility, OI=Open Interest
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
